import * as React from 'react';
import * as Images from 'pos/Images';

import { Result, SessionProvider } from 'core/infrastructure';
import { Content, Header, LargeDeviceOnly, PhoneOnly, PosScreen, ThemeManager, Footer, BackActionButton } from 'pos/ui';
import { Res, ProductForSaleDTO, Permission } from 'core/api';
import { debounce } from 'core/ui';
import * as SalesStore from '../SalesStore';
import { CatalogueFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { Modal } from 'core/ui-next/modal';
import z from 'zod';
import { HStack, VStack, Box, Text } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { SvgIcon } from 'core/ui-next';
import { Close } from 'core/img';
import { CheckBoxMenu } from 'core/ui-next/controls/CheckBoxMenu';
import { SearchProductByTextTable } from './sellProductsByTextCommandTable';
import { SearchProductByTextPhoneTable } from './sellProductsByTextCommandPhoneTable';


enum SortMode {
	SaleFormatName,
	Family,
	Barcode,
	Price
}

function sortProducts(products: ProductForSaleDTO[], sortMode: SortMode): ProductForSaleDTO[] {
	return products.sort((a, b) => {
		switch (sortMode) {
			case SortMode.SaleFormatName:
				return a.SaleFormatName.localeCompare(b.SaleFormatName);
			case SortMode.Family:
				return a.FamilyName.localeCompare(b.FamilyName);
			case SortMode.Barcode:
				return a.Barcode.localeCompare(b.Barcode);
			case SortMode.Price:
				// Si no tiene precio, se considera que va al final, por lo que al compararlo con otro producto con precio 0,
				// el producto sin precio debería situarse antes que el producto con precio 0.
				return (a.Price ?? -1) - (b.Price ?? -1);
		}
	});
}


const stateSchema = z.object({
	searchText: z.string().nullable(),
	showOnlySaleableProducts: z.boolean(),
	showOnlyThisPosProducts: z.boolean()
});

type FormState = z.infer<typeof stateSchema>;



function SearchProductByTextScreen(props: Modal.Actions<ProductForSaleDTO>) {

	const { cancel, close } = props;

	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input } = useControlsFor<FormState>();

	const initialState: FormState = {
		searchText: '',
		showOnlySaleableProducts: true,
		showOnlyThisPosProducts: true
	};

	const [products, setProducts] = React.useState<ProductForSaleDTO[]>([]);
	const [showOnlySaleableProducts, setShowOnlySaleableProducts] = React.useState<boolean>(true);
	const [showOnlyThisPosProducts, setShowOnlyThisPosProducts] = React.useState<boolean>(true);
	const [maxResults, setMaxResults] = React.useState<number>(1_000_000);
	const [sortMode, setSortMode] = React.useState<SortMode | undefined>(undefined);




	const onProductSelected = React.useCallback((product: ProductForSaleDTO) => {
		return close(product);
	}, [close]);


	const doSearch = React.useCallback(async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const { products, maxResults } = await CatalogueFacade.searchProductByText(SalesStore.getTicket().Id, result.value.searchText ?? '', result.value.showOnlyThisPosProducts);
		const filteredProducts = result.value.showOnlySaleableProducts ? products.filter(x => x.CannotBeSold === false) : products;
		setProducts(filteredProducts);
		setMaxResults(maxResults);
	}, []);


	const onShowOnlyThisPosProducts = React.useCallback(async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		setShowOnlyThisPosProducts(!showOnlyThisPosProducts);

		if (result.value.searchText && result.value.searchText.length > 0)
			await doSearch();
	}, [doSearch, showOnlyThisPosProducts]);

	const onShowOnlySaleableProducts = React.useCallback(async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		setShowOnlySaleableProducts(!showOnlySaleableProducts);

		if (result.value.searchText && result.value.searchText.length > 0)
			await doSearch();
	}, [doSearch, showOnlySaleableProducts]);


	const onSortBy = (sortMode: any) => {
		setSortMode(sortMode);
		if (sortMode !== undefined) {
			setProducts(sortProducts(products, sortMode));
		}
	};

	const onClear = React.useCallback(() => {
		formRef.current!.setValue('searchText', '');
		setProducts([]);
	}, []);

	const onCancel = React.useCallback(async () => {
		return cancel();
	}, [cancel]);


	const onShowDetails = async (product: ProductForSaleDTO) => await Command.execute('showProductDetails', product, SalesStore.getLocation());

	const hasSearchInOtherPosPermission = SessionProvider.hasPermission(Permission.Pos_SearchProductInOtherPos);

	return <PosScreen screenId='SELL_PRODUCT_BY_TEXT' size='medium' useKeyPad={false}>
		<Header title={Res.Web.SEARCH_PRODUCT_BY_TEXT()} image={Images.SearchByText} onClose={cancel} />
		<Content>
			<VStack w={'full'} padding={ThemeManager.isPhone() ? '3' : '6'} gap={'6'} h={'full'}>
				<VStack w={'full'} height={'full'}>
					<VStack width={'full'} alignItems={'stretch'} gap={'9'}>
						<VStack width={'full'} gap={'6'}>
							<Box w={'full'} textAlign={'center'}>
								<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.SEARCH_PRODUCT_BY_TEXT_TITLE()}</Text>
							</Box>
							<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ width: '100%' }}>
								<Box display={'flex'} flexDirection={ThemeManager.isPhone() ? 'column' : 'row'} gap={ThemeManager.isPhone() ? '4' : '9'} width={'full'}>
									<Box w={ThemeManager.isPhone() ? 'full' : '90'}>
										<Input
											linkTo={'searchText'}
											placeholder={Res.Web.SEARCH_PRODUCT_BY_TEXT_SEARCH_PLACEHOLDER()}
											startElement={<SvgIcon src={Images.Search} width={'4'} height={'4'} cursor={'pointer'} onClick={debounce(doSearch)} />}
											endElement={<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />}
											onKeyUp={debounce(doSearch)}
											borderRadius={'4xl'}
											borderColor={'neutral.600'}
											height={'10'}
										/>
									</Box>
									<HStack w={ThemeManager.isPhone() ? 'full' : '90'}>
										<SvgIcon src={Images.Filter} width={'6'} height={'6'} cursor={'pointer'} onClick={() => { }} />
										<CheckBoxMenu
											items={[
												{
													value: 'sellProductByTextSoldOnlyThisPosChk',
													linkTo: 'showOnlyThisPosProducts',
													title: Res.Web.SEARCH_PRODUCT_BY_TEXT_SOLD_ONLY_THIS_POS_CHK(),
													disabled: !hasSearchInOtherPosPermission,
													checked: hasSearchInOtherPosPermission ? showOnlyThisPosProducts : true,
													onCheckedChange: onShowOnlyThisPosProducts,
												},
												{
													value: 'sellProductByTextSoldOnlysaleableChk',
													linkTo: 'showOnlySaleableProducts',
													title: Res.Web.SEARCH_PRODUCT_BY_TEXT_SOLD_ONLY_SALEABLE_CHK(),
													checked: showOnlySaleableProducts,
													onCheckedChange: onShowOnlySaleableProducts,
												},
											]}
										/>
									</HStack>
								</Box>
							</Form>
						</VStack>
						<VStack w={'full'}>
							<LargeDeviceOnly>
								<SearchProductByTextTable
									products={products}
									maxResults={maxResults}
									sortMode={sortMode}
									onProductSelected={onProductSelected}
									onSortBy={onSortBy}
									onShowDetails={onShowDetails}
								/>
							</LargeDeviceOnly>
							<PhoneOnly>
								<SearchProductByTextPhoneTable
									products={products}
									maxResults={maxResults}
									sortMode={sortMode}
									onProductSelected={onProductSelected}
									onSortBy={onSortBy}
									onShowDetails={onShowDetails}
								/>
							</PhoneOnly>
						</VStack>
					</VStack>
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}


declare global {
	interface CommandType {
		sellProductByText: (compose: (product: ProductForSaleDTO, quantity?: number | undefined) => Promise<Result>) => Promise<Result>
	}
}

Command.register('sellProductByText', 'NO_PRECONDITION', async (compose: (product: ProductForSaleDTO, quantity?: number | undefined) => Promise<Result>): Promise<Result> => {
	const productForSaleResult = await Modal.show(SearchProductByTextScreen);
	if (!productForSaleResult.ok)
		return Result.error();

	return await compose(productForSaleResult.value);
});
